import { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";

// ui
import ButtonWithIcon from "ui-component/extended/ButtonWithIcon";
import EditIcon from "@mui/icons-material/Edit";
import Loader from "ui-component/Loader";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CustomPagination from "ui-component/CustomPagination";
import { Typography, Button } from "@mui/material";
import { StyledBarLoader } from "styles/globalStyles";
import { LinearProgress } from "@mui/material";
import AppEyeIcon from "ui-component/Icons/AppEyeIcon";

// project imports
import {
  ADS_LIST_ROUTE,
  EMAILS_ROUTE,
  REPORTS_ADS_ROUTE,
  REPORTS_ROUTE,
  SEARCH_AD_LIST,
  SEARCH_AD_LIST_ROUTE,
} from "constants/routes";
import {
  GET_FILTERED_ADS,
  GET_MANAGE_CATEGORIES,
  GET_SAVED_SEARCH_By_ID,
  GET_SAVED_SEARCH_LIST,
  GET_SIMILAR_SAVED_SEARCH_LIST,
} from "schemas/query";
import { formatDate } from "utils/date-format";

// redux
import { snackbarOpen } from "store/slice/snackbarSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";

import { DefaultEditIcon } from "ui-component/Icons/DefaultEditIcon";

import {
  selectSimilarSavedSearch,
  setLimit,
  setPage,
} from "store/slice/similarSavedSearchSlice";

import NoItemFound from "ui-component/NoItemFound";

interface SearchDetail {
  fullAddress: string;
}

interface SavedSearch {
  _id: string;
  adId: string;
  adType: string;
  status: string;
}

const SearchAdListTable = () => {
  const { page, limit, searchText, filter } = useAppSelector(
    selectSimilarSavedSearch
  );
  const [savedSearch, setSavedSearch] = useState<SavedSearch>();
  const [ads, setAds] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data, error, loading } = useQuery(GET_SAVED_SEARCH_By_ID, {
    variables: {
      body: {
        saveSearchId: id,
      },
    },
  });

  if (error) {
    dispatch(
      snackbarOpen({ open: true, severity: "error", message: error.message })
    );
  }

  useEffect(() => {
    if (data) {
      setSavedSearch(data.getSavedSearchById);
    }
  }, [data]);

  const { data: adsData } = useQuery(GET_FILTERED_ADS, {
    variables: {
      body: {
        type: savedSearch?.adType,
      },
    },
  });

  useEffect(() => {
    if (adsData) {
      setAds(adsData.getFilteredAds.items);
    }
  }, [adsData]);

  const handleClick = (id: string) => {
    navigate(`${ADS_LIST_ROUTE}/${id}`);
  };

  return (
    <>
      <StyledBarLoader>
        {loading && <LinearProgress color='inherit' />}
      </StyledBarLoader>
      {adsData?.getFilteredAds?.meta?.totalItems <= 0 && !loading ? (
        <NoItemFound />
      ) : (
        <TableContainer component={Paper}>
          <Table
            sx={{ minWidth: 650 }}
            aria-label='simple table'
            className='customDataTable'
          >
            <TableHead>
              <TableRow>
                <TableCell align='left'>
                  <Typography variant='menuCaption' noWrap>
                    AdID
                  </Typography>
                </TableCell>
                <TableCell align='left'>
                  <Typography variant='menuCaption' noWrap>
                    AD Type
                  </Typography>
                </TableCell>
                <TableCell align='left'>
                  <Typography variant='menuCaption' noWrap>
                    AD Status
                  </Typography>
                </TableCell>

                <TableCell align='left'>
                  <Typography variant='menuCaption' noWrap>
                    Action
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ads.map((item: SavedSearch) => (
                <TableRow
                  key={item._id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Typography
                      align='left'
                      variant='subMenuCaption'
                      sx={{ textTransform: "capitalize" }}
                    >
                      {item.adId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      align='left'
                      variant='subMenuCaption'
                      sx={{ textTransform: "capitalize" }}
                    >
                      {item.adType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      align='left'
                      variant='subMenuCaption'
                      sx={{ textTransform: "capitalize" }}
                    >
                      {item?.status}
                    </Typography>
                  </TableCell>

                  <TableCell align='left'>
                    <Button
                      variant='outlined'
                      startIcon={<AppEyeIcon />}
                      onClick={() => handleClick(item._id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {adsData?.getFilteredAds?.meta?.totalItems > 0 && (
        <CustomPagination
          totalPages={adsData?.getFilteredAds?.meta?.totalPages}
          setPage={setPage}
          setLimit={setLimit}
          currentPage={adsData?.getFilteredAds?.meta?.currentPage}
          itemCount={adsData?.getFilteredAds?.meta?.itemCount}
        />
      )}
    </>
  );
};

export default SearchAdListTable;
